import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import logo from "./assets/logo_icon.png";
import logo_wide from "./assets/logo_black_orange_150px.png";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro"; // <-- import styles to be used
import { useApprovalsInboxQuery } from "./views/approvals/hooks/useApprovalsQueries.js";
import { useCapabilities } from "./authz/useCapabilities";
import { CAPS } from "./authz/capabilities";
import { SidebarItem, Group } from "./components/nav/SidebarPrimitives.jsx";
import { useSettingsBadgeCount } from "./hooks/useSettingsBadgeCount";
// tabMemory (remembered sub-paths) is intentionally no longer used: sidebar
// items always open their section's first tab.

// getActiveTab maps the current route to the sidebar item that should highlight.
// Ordered startsWith checks; the policies check comes before the /budget-enforcement
// dashboard so /budget-enforcement/policies highlights Policies & Guardrails, not Control Center.
const getActiveTab = (location) => {
	const p = location.pathname;

	// Decisions — Policies & Guardrails (approval policies, delegations, budget guardrails)
	if (
		p.startsWith("/control-policies/") ||
		p.startsWith("/budget-enforcement/policies")
	) {
		return "Policies & Guardrails";
	}

	// 01 · Control — Control Center (exec dashboard + its sub-views)
	if (
		p.startsWith("/financial-control") ||
		p === "/budget-enforcement" ||
		p.startsWith("/forecast-predictability") ||
		p.startsWith("/ownership-accountability")
	) {
		return "Control Center";
	}

	// 01 · Control — Use Cases portfolio and detail
	if (p.startsWith("/use-cases")) return "Use Cases";

	// 02 · Spend — AI Usage Monitor is a tab under Cloud & AI Spend, so it keeps
	// that sidebar item highlighted.
	if (p.startsWith("/cost-analyzer") || p.startsWith("/cost-forecast") || p.startsWith("/ai-monitor")) return "Cloud & AI Spend";

	// 03 · Plan
	if (p.startsWith("/budgets")) return "Budgets";
	if (p.startsWith("/forecast-lab")) return "Forecasts";

	// 04 · Decisions
	if (p.startsWith("/approvals")) return "Approval Inbox";
	if (p.startsWith("/invoice-review")) return "Invoice Review";

	// 05 · Narrative — investigations + board reports
	if (p.startsWith("/investigations")) return "Investigations";
	if (p.startsWith("/analysis")) return "Analysis";
	if (
		p.startsWith("/report-list") ||
		p.startsWith("/report-editor") ||
		p.startsWith("/report-print") ||
		p.startsWith("/new-report")
	) {
		return "Custom Report";
	}
	if (p.startsWith("/board-reports")) return "Board Reports";

	// 07 · Optimize
	if (p.startsWith("/commitments")) return "Commitments";
	if (p.startsWith("/optimization-programme")) return "Optimization Programme";

	return "Control Center";
};

const Sidebar = (props) => {
	const [isOpen] = useState(true); // sidebar is always expanded (collapse control removed)
	const location = useLocation();
	const { data: inboxItems } = useApprovalsInboxQuery({ page_size: 100 });
	const approvalCount = Array.isArray(inboxItems) ? inboxItems.length : 0;
	// One aggregate for the Settings entry: every badge the settings sidebar shows
	// on its own items, summed (detected sources + last-run pipeline failures).
	const settingsBadgeCount = useSettingsBadgeCount();

	const [activeTab, setActiveTab] = useState(getActiveTab(location));
	const { capabilities } = useCapabilities();
	// Invoice Review needs CapInvoicesReview on the backend (see
	// backend_go/cmd/api/routes.go, /api/v1/invoices*), so gate the nav item
	// on that capability rather than an "admin" role.
	const canInvoiceReview = capabilities.includes(CAPS.INVOICES_REVIEW);

	// Update activeTab when location changes (e.g., programmatic navigation).
	useEffect(() => {
		setActiveTab(getActiveTab(location));
	}, [location]);

	const itemProps = { isOpen, activeTab, setActiveTab };

	return (
		<div
			// Pin the sidebar to the viewport height (h-* Tailwind classes are no-ops
			// here — theme.height is overridden — so set it inline) and keep it sticky
			// so it never scrolls with the page. The Brand stays fixed at the top; only
			// the nav list below scrolls when the groups are taller than the screen.
			style={{ height: "100vh" }}
			className={`flex-none flex flex-col bg-side sticky top-0 sidebar ${
				isOpen ? "min-w-[288px]" : "min-w-[80px]"
			} text-left`}
		>
			{/* Brand — fixed, never scrolls */}
			<div className="flex-none flex items-center gap-2 px-12 pt-6 pb-8">
				{isOpen
					? <img src={logo_wide} alt="InstantViewAI" width="140px" className="inline align-top" />
					: <img src={logo} alt="Logo" width="34px" className="inline align-top" />}
			</div>

			{/* Scrollable nav — owns the overflow so tall menus (e.g. admin) scroll
			    internally, keeping the logo above and the right-hand content in place.
			    min-h-0 lets this flex child shrink below its content height so it can scroll. */}
			<div className="flex-1 min-h-0 overflow-y-auto pb-4">
			<Group label="Control" isOpen={isOpen}>
				<SidebarItem
					url="/financial-control"
					{...itemProps}
					tabName="Control Center"
					sidebarIcon={icon({ name: "display-chart-up", family: "sharp", style: "light" })}
					tooltip="Control Center"
					tooltipDescription="Executive control dashboard — spend, ownership and forecast at a glance"
				/>
				<SidebarItem
					url="/use-cases"
					{...itemProps}
					tabName="Use Cases"
					sidebarIcon={icon({ name: "bullseye", family: "sharp", style: "light" })}
					tooltip="Use Cases"
					tooltipDescription="The AI initiatives you are spending on — cost, budget, forecast and value per use case"
				/>
				<SidebarItem
					url="/cost-analyzer"
					{...itemProps}
					tabName="Cloud & AI Spend"
					sidebarIcon={icon({ name: "chart-mixed", family: "sharp", style: "light" })}
					tooltip="Cloud & AI Spend"
					tooltipDescription="Explore cloud and AI spend across every dimension — providers, services, models and owners. AI Usage Monitor is a tab here."
				/>
			</Group>

			<Group label="Plan" isOpen={isOpen}>
				<SidebarItem
					url="/budgets"
					{...itemProps}
					tabName="Budgets"
					sidebarIcon={icon({ name: "scale-balanced", family: "sharp", style: "light" })}
					tooltip="Budgets"
					tooltipDescription="View and manage budgets"
				/>
				<SidebarItem
					url="/forecast-lab"
					{...itemProps}
					tabName="Forecasts"
					sidebarIcon={icon({ name: "chart-line", family: "sharp", style: "light" })}
					tooltip="Forecasts"
					tooltipDescription="Cost and usage forecasts, scenarios and model accuracy"
				/>
			</Group>

			<Group label="Decisions" isOpen={isOpen}>
				<SidebarItem
					url="/approvals"
					{...itemProps}
					tabName="Approval Inbox"
					sidebarIcon={icon({ name: "clipboard-check", family: "sharp", style: "light" })}
					tooltip="Approval Inbox"
					tooltipDescription="Pending approval requests and decision flows"
					badge={approvalCount}
				/>
				<SidebarItem
					url="/control-policies/approval-policies"
					{...itemProps}
					tabName="Policies & Guardrails"
					sidebarIcon={icon({ name: "shield-check", family: "sharp", style: "light" })}
					tooltip="Policies & Guardrails"
					tooltipDescription="Approval policies and budget guardrails"
				/>
				{canInvoiceReview && (
					<SidebarItem
						url="/invoice-review"
						{...itemProps}
						tabName="Invoice Review"
						sidebarIcon={icon({ name: "file-invoice", family: "sharp", style: "light" })}
						tooltip="Invoice review"
						tooltipDescription="Review and reconcile ingested invoices"
					/>
				)}
			</Group>

			<Group label="Narrative" isOpen={isOpen}>
				<SidebarItem
					url="/board-reports"
					{...itemProps}
					tabName="Board Reports"
					sidebarIcon={icon({ name: "presentation-screen", family: "sharp", style: "light" })}
					tooltip="Board Reports"
					tooltipDescription="Create and manage board-ready monthly summary reports"
				/>
				<SidebarItem
					url="/investigations"
					{...itemProps}
					tabName="Investigations"
					sidebarIcon={icon({ name: "magnifying-glass", family: "sharp", style: "light" })}
					tooltip="Investigations"
					tooltipDescription="Explain and document why cost, usage, forecasts or budgets changed"
				/>
				<SidebarItem
					url="/analysis"
					{...itemProps}
					tabName="Analysis"
					sidebarIcon={icon({ name: "message-lines", family: "sharp", style: "light" })}
					tooltip="Analysis"
					tooltipDescription="Ask questions and get instant answers about cost, usage and forecasts"
				/>
			</Group>

			<Group label="Optimize" isOpen={isOpen}>
				<SidebarItem
					url="/commitments"
					{...itemProps}
					tabName="Commitments"
					sidebarIcon={icon({ name: "file-contract", family: "sharp", style: "light" })}
					tooltip="Commitments"
					tooltipDescription="Active GCP committed-use discounts and renewal exposure"
				/>
				<SidebarItem
					url="/optimization-programme"
					{...itemProps}
					tabName="Optimization Programme"
					sidebarIcon={icon({ name: "lightbulb-on", family: "sharp", style: "light" })}
					tooltip="Optimization Programme"
					tooltipDescription="Track recommender candidates promoted into committed and realized savings"
				/>
			</Group>
			</div>

			{/* Pinned footer — Settings shell entry (like bill.com). Sits outside the
			    scrollable nav so it stays visible; the badge aggregates every settings
			    item that needs attention, so a pipeline failure is visible from any page. */}
			<div className="flex-none border-t border-[hsl(214_22%_84%)] pt-2 pb-3">
				<SidebarItem
					url="/settings"
					{...itemProps}
					tabName="Settings"
					sidebarIcon={icon({ name: "gear", family: "sharp", style: "light" })}
					tooltip="Settings"
					tooltipDescription="Configure the application — cost model, data, access and system"
					badge={settingsBadgeCount}
				/>
			</div>
		</div>
	);
};

export default Sidebar;
